import { InfoIcon } from '@chakra-ui/icons';
import {
  FormControl,
  HStack,
  Icon,
  ListItem,
  Stack,
  Text,
  Tooltip,
  UnorderedList,
} from '@chakra-ui/react';
import { PhotographIcon } from '@heroicons/react/outline';
import axios from 'axios';
import { useState } from 'react';
import { FileRejection } from 'react-dropzone';
import { Controller, useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { TranslationValues, useTranslations } from 'use-intl';

import { FormErrors } from '@blockpulse3/data/shared';
import {
  FundraisingDocumentType,
  IdentityType,
  useDeleteSubscriptionDocumentMutation,
  useGetSubscriptionDocumentQuery,
  useGetSubscriptionQuery,
} from '@blockpulse3/graphql/hooks';
import { DropzoneInput, ErrorMessage } from '@blockpulse3/ui/commons';

import { SubscriptionDetailsForm, TooltipKeys } from './types';

type Props = {
  errorValues: TranslationValues | undefined;
};

export function FundsSourceUpload({ errorValues }: Props): JSX.Element {
  const i18nFundsSourceUpload = useTranslations('FundsSourceUpload');
  const i18nTooltipIndividual = useTranslations('FundsSourceUpload.Tooltip.Individual');
  const i18nTooltipCompany = useTranslations('FundsSourceUpload.Tooltip.Company');

  const [isFileLoading, setIsFileLoading] = useState<boolean>(false);

  const { subscriptionId = '' } = useParams();
  const subscriptionReq = useGetSubscriptionQuery({
    variables: { subscriptionId },
  });

  const { data, refetch } = useGetSubscriptionDocumentQuery({
    variables: { subscriptionId, documentType: FundraisingDocumentType.FUNDS_SOURCE_CERTIFICATE },
    onCompleted: () => {
      setValue('fundsSourceCertificate', fundsSourceCertificate?.id || '');
    },
  });
  const fundsSourceCertificate = data?.getSubscriptionDocument?.document;

  const [deleteDocument] = useDeleteSubscriptionDocumentMutation();

  /* ** Retrieve all hook methods ** */
  const { setError, clearErrors, control, formState, setValue } =
    useFormContext<SubscriptionDetailsForm>();

  const certificateFileName = fundsSourceCertificate
    ? {
        [fundsSourceCertificate.id]: new File(
          [fundsSourceCertificate.title],
          fundsSourceCertificate.title,
        ),
      }
    : {};

  /* ** Method to upload a file using axios ** */
  const handleFileUpload = async (acceptedFiles: File[]): Promise<void> => {
    if (!subscriptionId) return;

    const formData = new FormData();
    formData.append('subscriptionDocument', acceptedFiles[0]);
    formData.append('subscriptionId', subscriptionId);
    formData.append('documentType', FundraisingDocumentType.FUNDS_SOURCE_CERTIFICATE);

    setIsFileLoading(true);
    await axios
      .post(
        process.env['NX_API_CONTROLLER_ENDPOINT'] + '/subscriptions/uploadSubscriptionDocument',
        formData,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'multipart/form-data',
          },
        },
      )
      .catch(() => {
        setError('fundsSourceCertificate', {
          type: 'custom',
          message: FormErrors.DropzoneDefault,
        });
      });

    await refetch();
    setIsFileLoading(false);
  };

  /* ** Upload file handler ** */
  const handleFileDrop = (acceptedFiles: File[], fileRejections: FileRejection[]): void => {
    clearErrors('fundsSourceCertificate');
    if (fileRejections.length === 0) {
      handleFileUpload(acceptedFiles);
    } else {
      const error = fileRejections[0].errors[0].code;
      switch (error) {
        case 'file-too-large': {
          setError('fundsSourceCertificate', { type: 'custom', message: FormErrors.FileTooLarge });
          break;
        }
        default: {
          setError('fundsSourceCertificate', {
            type: 'custom',
            message: FormErrors.DropzoneDefault,
          });
          break;
        }
      }
    }
  };

  /* ** Delete file handler ** */
  const handleFileDelete = (): void => {
    if (!subscriptionId) return;

    deleteDocument({
      variables: {
        deleteSubscriptionDocumentInput: {
          subscriptionId,
          documentType: FundraisingDocumentType.FUNDS_SOURCE_CERTIFICATE,
        },
      },
      onCompleted: () => {
        refetch();
      },
    });
  };

  const subscription = subscriptionReq?.data?.subscription;
  const isIndividual = subscription?.identity.type === IdentityType.INDIVIDUAL;

  return (
    <Stack>
      <HStack>
        <Text fontSize="md" fontWeight="600">
          {i18nFundsSourceUpload('Title')}
        </Text>
        <Tooltip
          hasArrow
          placement="right"
          label={
            <UnorderedList>
              {Array.from({ length: isIndividual ? 6 : 4 }).map((_, index) => (
                <ListItem key={index}>
                  {(isIndividual ? i18nTooltipIndividual : i18nTooltipCompany)(
                    `ListItem${index + 1}` as TooltipKeys,
                  )}
                </ListItem>
              ))}
            </UnorderedList>
          }
        >
          <Icon as={InfoIcon} color="gray.600" />
        </Tooltip>
      </HStack>
      <Stack bgColor="gray.50" p="4">
        <Controller
          control={control}
          name="fundsSourceCertificate"
          render={(): JSX.Element => (
            <FormControl>
              <DropzoneInput
                accept={{ 'application/pdf': [], 'image/png': [], 'image/jpeg': [] }}
                files={certificateFileName}
                isLoading={isFileLoading}
                maxFiles={1}
                subTitle=".pdf .png .jpeg"
                icon={
                  <Icon as={PhotographIcon} boxSize="42px" color="gray.400" strokeWidth="1px" />
                }
                onDelete={handleFileDelete}
                onDrop={handleFileDrop}
              />
              <ErrorMessage error={formState.errors?.fundsSourceCertificate} values={errorValues} />
            </FormControl>
          )}
        />
      </Stack>
    </Stack>
  );
}
